import './styles.css';
import { useState, useRef, useEffect, memo, useCallback, useMemo } from 'react';
import {
  View,
  Text,
} from '@smarterservices/smarter-ui';
import {
  ScreenReaderContent,
  AppNav,
  IconHamburgerLine,
} from '@instructure/ui';
import Communication from './Communication';
import Checkin from './CheckIn';
import SessionInfo from './SessionInfo';
import Journey from './Journey';
import AnnotationsList from './Annotations';
import ComputerInfo from './ComputerInfo';
import Identity from './Identity';
import IncidentReport from './IncidentReport';
import { getStore, useCheckinStore, useLayoutStore, useExamSessionStore } from '../../stores/StoreContext';
import { useSecondPaneContext } from '../Common/SplitView/SplitViewContext';

/**
 * SessionDetails
 *
 * This component displays the details of a session, including the session
 * information, check-in, interactions, chat, and journey.
 *
 * @returns {JSX.Element} The component.
 */
const SessionDetails = () => {
  const LayoutStore = getStore('layoutStore');
  const { height } = useSecondPaneContext();

  const totalItemsCount = 7; // Total number of navigation items
  const navigationHeight = 40;
  const contentHeight = height - navigationHeight;

  const [visibleItemsCount, setVisibleItemsCount] = useState(totalItemsCount);
  const [selectedNavItem, setSelectedNavItem] = useState('flags'); // Default to checkin

  // Only re-render when approval status changes
  const isApproved = useCheckinStore((state) => state.isApproved);
  const isCollapsed = useLayoutStore((state) => state?.detailsSplitView?.isCollapsed || false);
  const isFullScreen = useLayoutStore((state) => state?.isFullScreen || false);


  const incidentReport = useExamSessionStore((state) => state.incidentReport);
  // Memoize the update handler to prevent unnecessary re-renders
  const handleUpdate = useCallback(({ visibleItemsCount }) => {
    setVisibleItemsCount(visibleItemsCount);
  }, []);

  // Track if this is the initial mount
  const isInitialMount = useRef(true);

  // Handle nav item selection with useCallback to prevent unnecessary re-renders
  const handleNavItemClick = useCallback(
    (key) => {
      // Update the selected tab
      setSelectedNavItem(key);

      // Only expand the split view when a tab is explicitly clicked
      // This won't run on re-renders, only on actual click events
      if (
        LayoutStore.detailsSplitView &&
        LayoutStore.detailsSplitView.isCollapsed
      ) {
        setTimeout(() => {
          LayoutStore.detailsSplitView.expand();
        }, 50);
      }
    },
    [LayoutStore]
  );

  // Only run this effect once on initial mount
  useEffect(() => {
    // Only expand on initial mount
    if (isInitialMount.current && LayoutStore.detailsSplitView) {
      if (LayoutStore.detailsSplitView.isCollapsed) {
        setTimeout(() => {
          LayoutStore.detailsSplitView.expand();
        }, 50);
      }
      isInitialMount.current = false;
    }
  }, []); // Empty dependency array means this only runs once on mount

  useEffect(() => {
    LayoutStore.setSessionDetailsHeight(contentHeight);
  }, [contentHeight, LayoutStore]);

  const sessionDetailsRef = useRef(null);

  // Memoize the navigation options to prevent unnecessary re-renders
  const navOptions = useMemo(
    () => ({
      info: {
        label: 'Session Info',
        notification: { color: 'danger', count: 1 },
        component: <SessionInfo />,
      },
      checkin: {
        label: 'Check-in',
        notification: { color: 'danger', count: 1 },
        component: <Checkin />,
      },
      communication: {
        label: 'Communication',
        notification: { color: 'danger', count: 1 },
        component: <Communication />,
      },
      flags: {
        label: 'Flags',
        notification: { color: 'danger', count: 1 },
        component: <AnnotationsList />,
      },
      identity: {
        label: 'Identification',
        notification: { color: 'danger', count: 1 },
        component: <Identity />,
      },
      incidentReport: {
        label: 'Incident Report',
        component: <IncidentReport />,
      },
      journey: {
        label: 'Journey',
        notification: { color: 'danger', count: 1 },
        component: <Journey />,
      },
      computerInfo: {
        label: 'Computer Info',
        notification: { color: 'danger', count: 1 },
        component: <ComputerInfo />,
      },
    }),
    []
  ); // Empty dependency array since these don't change

  // Memoize the truncate label renderer to prevent unnecessary re-renders
  const renderTruncateLabel = useCallback(() => {
    const hiddenItemsCount = totalItemsCount - visibleItemsCount;
    if (visibleItemsCount >= 2) {
      return `${hiddenItemsCount} More`;
    } else {
      return (
        <span>
          <IconHamburgerLine size="small" inline={false} />
          <ScreenReaderContent>{`${hiddenItemsCount} More`}</ScreenReaderContent>
        </span>
      );
    }
  }, [visibleItemsCount, totalItemsCount]);

  // Memoize the nav item renderer to prevent unnecessary re-renders
  const navItems = useMemo(() => {
    const itemsToDisplay = ['info', 'flags', 'identity'];
    if (incidentReport && !incidentReport.draft) {
      itemsToDisplay.push('incidentReport');
    }

    const items = Object.keys(navOptions).map((key) => {
      // ensure the user has the ability to view the tab
      if (!itemsToDisplay.includes(key)) {
        return null;
      }
      return (<AppNav.Item
        key={key}
        isSelected={selectedNavItem === key}
        onClick={() => handleNavItemClick(key)}
        renderLabel={
          <View
            as="span"
            padding="xx-small small"
            className={`tab-item ${selectedNavItem === key ? 'selected' : ''}`}
            borderWidth={
              selectedNavItem === key ? 'none none medium none' : 'none'
            }
            borderColor="primary"
            style={{
              position: 'relative',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <Text
              size="small"
              weight={selectedNavItem === key ? 'bold' : 'normal'}
              color={isCollapsed ? 'primary-inverse' : selectedNavItem === key ? 'primary' : 'secondary'}
            //color={selectedNavItem === key ? 'primary' : 'secondary'}
            >
              {navOptions[key].label}
              {/* TODO: Add proper notifications
                {navOptions[key].notification && selectedNavItem !== key && (
                  <Badge
                    type="notification"
                    standalone
                    variant={navOptions[key].notification.color || 'danger'}
                    margin="none none none xx-small"
                    count={navOptions[key].notification.count}
                  />
                )}
                */}
            </Text>
          </View>
        }
        notification={navOptions[key].notification}
      />);
    }
    );

    return items;


  }, [navOptions, selectedNavItem, handleNavItemClick, incidentReport]);

  // Memoize the content components to prevent unnecessary re-renders
  const contentComponents = useMemo(() => {
    return Object.keys(navOptions).map((key) => (
      <div
        key={key}
        style={{
          display: selectedNavItem === key ? 'block' : 'none',
          height: '100%',
          overflow: 'auto',
        }}
      >
        {navOptions[key].component}
      </div>
    ));
  }, [navOptions, selectedNavItem]);

  // Use a ref callback instead of an inline function
  const setSessionDetailsRef = useCallback((el) => {
    sessionDetailsRef.current = el;
  }, []);

  return (
    <>
      <View
        as="div"
        position="relative"
        margin="none"
        padding="none"
        withVisualDebug={false} /* Set to false in production */
        className="session-details"
        background={isCollapsed ? 'primary-inverse' : 'secondary'}
        elementRef={setSessionDetailsRef}
      >
        <AppNav
          screenReaderLabel="Session Details Navigation"
          visibleItemsCount={5}
          onUpdate={handleUpdate}
          themeOverride={{
            height: `${navigationHeight}px`,
          }}
          renderTruncateLabel={renderTruncateLabel}
        >
          {navItems}
        </AppNav>
        {/* Load the component in this view... */}
        <View
          as="div"
          position="relative"
          margin="none"
          padding="x-small"
          height={`${contentHeight}px`}
          overflowY="auto"
          overflowX="hidden"
          background="primary"
        >
          {/* Render all components but only show the selected one */}
          {contentComponents}
        </View>
      </View>
    </>
  );
};

export default memo(SessionDetails, (prevProps, nextProps) => {
  return prevProps.isFullScreen === nextProps.isFullScreen;
});
